import { AddCircle, ExpandCircleDown, RemoveCircle } from "@mui/icons-material";
import {
  Box,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Paper,
  Card,
} from "@mui/material";
import {
  EnforcementSchedule,
  EntityPanelActionHandler,
  EntityTypes,
  Rule,
  Status,
  TimeRange,
  TransferListDataSet,
} from "types";
import {
  ChangeEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createTransferListArrays, formatViolationsString } from "utils";

import ExpandMore from "components/buttons/ExpandMore";
import JobTypeChip from "components/chips/JobTypeChip";
import MobileTransferList from "components/lists/MobileTransferList";
import RuleMobileTransferListItem from "components/lists/listItems/RuleMobileTransferListItem";
import dayjs from "dayjs";
import { useLocation, useFetcher, useOutletContext } from "react-router";
import { useBackButton } from "hooks/useBackButton";
import { useTranslation } from "react-i18next";
import { AppContext } from "context/appContext";
import AuturaTextField from "components/inputs/AuturaTextField";
import { getHandler } from "./RulesetsPanel";
import AuturaDropdown from "components/inputs/AuturaDropdown";
import FormFrame from "components/layout/FormFrame";
import { RulesetContext } from "./RulesetsPanel";
import { ValidatorForm } from "react-material-ui-form-validator";
import DetailsHeader from "components/layout/DetailsHeader";
import {
  AUTURA_GRAYSCALE,
  TEXT_PRIMARY,
} from "context/themeContext";

export const RulesetForm = (): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme<Theme>();
  const { state } = useContext(AppContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [expanded, setExpanded] = useState<number | false>(false);

  const {
    ruleset,
    setRuleset,
    tenantRules,
    onBack,
    formMode,
    displayMode,
    fetcherKey,
    rulesets,
  } = useOutletContext<RulesetContext>();

  const fetcher = useFetcher({ key: fetcherKey });
  const { pathname } = useLocation();
  const [rulesDataSet, setRulesDataSet] = useState<TransferListDataSet<Rule>>(
    createTransferListArrays(tenantRules, ruleset.rules),
  );

  const canAddRemove = state.entityPermissions[EntityTypes.Rule].update;

  useEffect(() => {
    setRuleset({ ...ruleset, rules: rulesDataSet.included });
  }, [rulesDataSet.included]);

  // handle the browser back button
  useBackButton(onBack);

  const handleAddRemoveRule = (rule: Rule, included: boolean) => {
    setExpanded(false);
    if (included) {
      // remove the rule from the included array
      setRulesDataSet({
        included: rulesDataSet.included.filter((r) => r.id !== rule.id),
        excluded: [...rulesDataSet.excluded, rule],
      });
    } else {
      // remove the rule from the excluded array
      setRulesDataSet({
        included: [...rulesDataSet.included, rule],
        excluded: rulesDataSet.excluded.filter((r) => r.id !== rule.id),
      });
    }
  };
  const handleExpandClick = (rule: Rule) => {
    setExpanded(expanded === rule.id ? false : rule.id);
  };

  const renderRuleItem = (rule: Rule, included: boolean) => {
    const isExpanded = expanded === rule.id;

    return (
      <Paper
        key={`${rule.id}-${JSON.stringify(rule.ancestry)}`}
        sx={{
          overflow: "hidden",
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          backgroundColor: "white",
        }}
      >
        <ListItem
          sx={{
            height: 60,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          secondaryAction={
            <Stack
              direction="row"
              spacing={4}
              alignItems="center"
              justifyContent="space-between"
            >
              {canAddRemove && (
                <IconButton
                  onClick={() => handleAddRemoveRule(rule, included)}
                  size={"medium"}
                  sx={{
                    p: 0,
                    "&:hover": {
                      outline: "1px solid",
                      outlineColor: included
                        ? theme.palette.error.main
                        : theme.palette.success.main,
                    },
                  }}
                >
                  {included ? (
                    <RemoveCircle
                      htmlColor={theme.palette.error.contrastText}
                      sx={{
                        p: 0,
                        background: theme.palette.error.main,
                        borderRadius: "90px",
                      }}
                    />
                  ) : (
                    <AddCircle
                      htmlColor={theme.palette.success.contrastText}
                      sx={{
                        p: 0,
                        background: theme.palette.success.main,
                        borderRadius: "90px",
                      }}
                    />
                  )}
                </IconButton>
              )}
              <ExpandMore
                sx={{
                  p: 0,
                  "&:hover": {
                    outline: "1px solid",
                    outlineColor: theme.palette.primary.main,
                  },
                }}
                onClick={() => handleExpandClick(rule)}
                expand={isExpanded}
                aria-expanded={isExpanded}
                aria-label="show more"
              >
                <ExpandCircleDown
                  htmlColor={"white"}
                  sx={{
                    background: theme.palette.primary.main,
                    borderRadius: "90px",
                  }}
                />
              </ExpandMore>
            </Stack>
          }
        >
          <ListItemText
            primary={
              <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                {rule.name}
              </Typography>
            }
            secondary={
              <Typography variant="body2">{rule.description}</Typography>
            }
          />
        </ListItem>
        <Collapse in={isExpanded} timeout={"auto"} unmountOnExit>
          <Box sx={{ p: 2, backgroundColor: "inherit" }}>
            <Grid container width={"100%"} spacing={2}>
              <Grid size={12}>
                <Box
                  sx={{
                    display: "flex",
                    width: "%100",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                  }}
                >
                  <JobTypeChip
                    key={rule.data.ruleType}
                    type={rule.data.ruleType}
                    size="medium"
                    sx={{ mx: 0.25 }}
                  />
                </Box>
              </Grid>
              <Grid size={12}>
                <TextField
                  label={t("violation.label_plural", { ns: "entities" })}
                  fullWidth
                  multiline
                  variant="standard"
                  margin="dense"
                  type="text"
                  defaultValue={formatViolationsString(rule.data.violations)}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Stack
                  direction={"row"}
                  sx={{ pb: 2 }}
                  spacing={1}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  {Object.keys(rule.data.enforcementSchedule).map((d) => {
                    const day =
                      rule.data.enforcementSchedule[
                        d.toLowerCase() as keyof EnforcementSchedule
                      ];
                    return (
                      <Stack key={rule.id + d} flexDirection="column" flex={1}>
                        <Typography variant={"h6"} align={"center"}>
                          {t(`${d.toLowerCase()}`)}
                        </Typography>
                        <Divider orientation="horizontal" />
                        {day.length ? (
                          day.map((tr: TimeRange, i: number) => {
                            return (
                              <Stack
                                direction={"column"}
                                key={i}
                                sx={{ mt: 1 }}
                              >
                                <Typography
                                  key={"start"}
                                  variant="body2"
                                  align={"center"}
                                >
                                  {dayjs(tr.startTime, "HHmm").format("h:mm A")}
                                </Typography>
                                <Typography
                                  key={"dash"}
                                  variant="body2"
                                  align={"center"}
                                >
                                  {" - "}
                                </Typography>
                                <Typography
                                  key={"end"}
                                  variant="body2"
                                  align={"center"}
                                >
                                  {dayjs(tr.endTime, "HHmm").format("h:mm A")}
                                </Typography>
                              </Stack>
                            );
                          })
                        ) : (
                          <Typography
                            variant="body2"
                            align={"center"}
                            key={d + "empty"}
                          >
                            {" - "}
                          </Typography>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>
    );
  };

  const handleSubmit = () => {
    if (formMode === "edit") {
      fetcher.submit(
        {
          body: JSON.stringify(ruleset),
          handler: EntityPanelActionHandler.UPDATE,
        },
        {
          method: "PUT",
          action: pathname,
          encType: "application/json",
        },
      );
      return;
    }

    if (formMode === "create") {
      const newRulesets = [...rulesets, ruleset];
      fetcher.submit(
        {
          body:
            displayMode === "tenant"
              ? JSON.stringify(ruleset)
              : JSON.stringify(newRulesets),
          handler: getHandler({ displayMode, formMode }),
        },
        {
          method: "POST",
          action: pathname,
          encType: "application/json",
        },
      );
    }
  };

  const submitButtonText = useMemo(() => {
    switch (formMode) {
      case "edit":
        return t("buttons.update", {
          name: t("ruleset.label", { ns: "entities" }),
        });
      case "create":
        return t("buttons.create", {
          name: t("ruleset.label", { ns: "entities" }),
        });
      default:
        return "";
    }
  }, [formMode]);

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormFrame loading={fetcher.state !== "idle"}>
        <DetailsHeader
          title={ruleset.name}
          onCancel={onBack}
          submitLabel={submitButtonText}
          readonly={!canAddRemove}
          disableSubmit={fetcher.state !== "idle"}
        />
        <Grid container width={"100%"} spacing={1.5} sx={{ pb: 3, mt: 3 }}>
          <Grid
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <AuturaTextField
              name="name"
              label={t("ruleset.name", { ns: "entities" })}
              value={ruleset.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setRuleset({ ...ruleset, name: e.target.value })
              }
              required
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <AuturaTextField
              name="description"
              label={t("ruleset.description", { ns: "entities" })}
              value={ruleset.description}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setRuleset({ ...ruleset, description: e.target.value })
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <AuturaDropdown
              name="status"
              label={t("ruleset.status", { ns: "entities" })}
              value={ruleset.status}
              setValue={(e: ChangeEvent<HTMLInputElement>) =>
                setRuleset({ ...ruleset, status: e.target.value as Status })
              }
              options={[Status.Active, Status.Disabled]}
              readonly={!canAddRemove}
              disabled={formMode === "create"}
            />
          </Grid>
        </Grid>
      </FormFrame>
      {isMobile ? (
        <MobileTransferList
          dataSet={rulesDataSet}
          setDataSet={setRulesDataSet}
          ListItemComponent={RuleMobileTransferListItem}
          includedTitle={t("labels.included_rules", { ns: "rules" })}
          excludedTitle={t("labels.excluded_rules", { ns: "rules" })}
        />
      ) : (
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={4}>
            <Card
              sx={{
                border: `1px solid ${AUTURA_GRAYSCALE[400]}`,
                height: `calc(100dvh - 472px)`,
                overflow: "hidden",
                display: "flex",
                flex: 1,
                flexDirection: "column",
                backgroundColor: "white",
              }}
              raised
              elevation={4}
            >
              <Box
                sx={{
                  p: 1,
                  backgroundColor: AUTURA_GRAYSCALE[200],
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography variant="h4" color={TEXT_PRIMARY} align="center">
                  {t("labels.excluded_rules", { ns: "rules" })}
                </Typography>
              </Box>
              <Box sx={{ overflowY: "scroll", height: "100%" }}>
                <List disablePadding dense>
                  {rulesDataSet.excluded.map((rule) =>
                    renderRuleItem(rule, false),
                  )}
                </List>
              </Box>
            </Card>
            <Card
              sx={{
                border: `1px solid ${AUTURA_GRAYSCALE[400]}`,
                height: `calc(100dvh - 472px)`,
                overflow: "hidden",
                display: "flex",
                flex: 1,
                flexDirection: "column",
                backgroundColor: "white",
              }}
              raised
              elevation={4}
            >
              <Box
                sx={{
                  p: 1,
                  backgroundColor: AUTURA_GRAYSCALE[200],
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography variant="h4" color={TEXT_PRIMARY} align="center">
                  {t("labels.included_rules", { ns: "rules" })}
                </Typography>
              </Box>
              <Box sx={{ overflowY: "scroll", height: "100%" }}>
                <List disablePadding dense>
                  {rulesDataSet.included.map((rule) =>
                    renderRuleItem(rule, true),
                  )}
                </List>
              </Box>
            </Card>
          </Stack>
        </Box>
      )}
    </ValidatorForm>
  );
};
